import { ExternalLink, LayoutGrid } from 'lucide-react';
import { APPLICATION_STATUS, type Application } from '@electrodavisan/shared';
import { ApplicationStatusBadge, Button, Card } from '../../components/ui';

/**
 * Tarjeta de una aplicacion del launcher.
 *
 * ## Como esta ordenada
 *
 * El logotipo grande y centrado arriba, y debajo el texto. Es lo contrario de
 * una lista: aqui no se lee, se RECONOCE. Quien entra al portal ya sabe a que
 * aplicacion va y lo que busca es su icono, asi que el icono tiene que ser lo
 * primero y lo mas grande, no una miniatura al lado de un titulo.
 *
 * ## Que NO aparece
 *
 * El modo de acceso (OIDC / EXTERNAL). Es un detalle de como esta conectada la
 * aplicacion por dentro, no algo que ayude a decidir si abrirla: a quien la usa
 * le da igual que la sesion se resuelva con un protocolo o con otro, y lo unico
 * que conseguia era ocupar una linea entera con una sigla. Sigue estando en la
 * pantalla de administracion, que es donde importa.
 *
 * ## Como se abre
 *
 * "Abrir aplicacion" es un enlace normal a la `launch_url`. No se adjunta
 * ningun token ni parametro de sesion: la aplicacion de destino hara su
 * propio flujo OIDC contra Keycloak y, como ya existe sesion SSO, el usuario
 * entrara sin volver a escribir sus credenciales.
 */
export function ApplicationCard({ application }: { application: Application }): JSX.Element {
  const isOpenable =
    application.status === APPLICATION_STATUS.ACTIVE && Boolean(application.launchUrl);

  return (
    <Card className="flex flex-col items-center p-5 text-center transition-colors hover:border-border-strong">
      {/*
        El estado va arriba a la derecha, FUERA del flujo central.

        Es la unica pieza que no esta centrada, y a proposito: en el centro
        competiria con el nombre, y lo que dice —«Disponible»— solo importa
        cuando NO lo esta.
      */}
      <div className="flex w-full justify-end">
        <ApplicationStatusBadge status={application.status} />
      </div>

      {/*
        `-mt-2` sube el logotipo hasta la altura del distintivo de estado: sin
        esto, la fila del estado le roba su alto entero a la tarjeta y el icono
        queda hundido.
      */}
      {application.iconUrl ? (
        /*
         * `object-contain` y no `object-cover`.
         *
         * `cover` recorta para rellenar el cuadrado: a un logotipo le corta
         * los bordes, que es justo la parte que lo hace reconocible. Con
         * `contain` se ve entero y, si no es cuadrado, sobra hueco en vez de
         * perderse dibujo.
         *
         * Sin `rounded-xl` tampoco: estos iconos ya traen su propia forma
         * —el de ED Calendar es un cuadrado redondeado— y redondearlos otra
         * vez les come las esquinas.
         */
        <img
          src={application.iconUrl}
          alt=""
          className="-mt-2 h-20 w-20 shrink-0 object-contain"
          loading="lazy"
        />
      ) : (
        <span
          aria-hidden
          className="-mt-2 flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary-bright"
        >
          <LayoutGrid className="h-9 w-9" />
        </span>
      )}

      <h3 className="mt-3 w-full truncate text-[15px] font-semibold text-text-primary">
        {application.name}
      </h3>
      {application.category ? (
        <p className="mt-0.5 w-full truncate text-[13px] text-text-muted">{application.category}</p>
      ) : null}

      {/*
        `min-h` fija dos lineas aunque el texto ocupe una sola: sin ella, las
        tarjetas de una misma fila acaban con el boton a distinta altura y la
        rejilla se ve descuadrada.
      */}
      <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-[13px] leading-relaxed text-text-secondary">
        {application.shortDescription ?? 'Sin descripcion.'}
      </p>

      {/* `mt-auto` empuja el boton abajo del todo, pase lo que pase arriba. */}
      <div className="mt-auto w-full pt-4">
        {isOpenable ? (
          <a
            href={application.launchUrl ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button
              size="sm"
              className="w-full"
              rightIcon={<ExternalLink className="h-3.5 w-3.5" aria-hidden />}
            >
              Abrir aplicacion
            </Button>
          </a>
        ) : (
          <Button size="sm" variant="secondary" className="w-full" disabled>
            {application.status === APPLICATION_STATUS.MAINTENANCE
              ? 'En mantenimiento'
              : application.status === APPLICATION_STATUS.COMING_SOON
                ? 'Proximamente'
                : 'No disponible'}
          </Button>
        )}
      </div>
    </Card>
  );
}
